import { config } from '@/config';
import { addSeconds, isBefore } from 'date-fns';

export interface ICacheStorage<T> {
    date: string;
    data: T;
}

const hasExpired = <T>(cacheData: ICacheStorage<T>, revalidate: number): boolean => {
    const currentDateTime = new Date();
    const expirationDateTime = cacheData.date
        ? addSeconds(new Date(cacheData.date), revalidate)
        : undefined;
    return !expirationDateTime || isBefore(expirationDateTime, currentDateTime);
};
export const createCache = <T>({
    maxSize = 7000,
    revalidate = config.timeout.revalidate,
}: { maxSize?: number; revalidate?: number } = {}) => {
    const cacheStorage = new Map<string, ICacheStorage<T>>();
    const lruList: string[] = [];

    const removeOldestCache = () => {
        const oldest = lruList.shift();
        if (oldest) {
            cacheStorage.delete(oldest);
        }
    };

    const set = (hash: string, value: T) => {
        lruList.push(hash);
        if (lruList.length > maxSize) {
            removeOldestCache();
        }
        cacheStorage.set(hash, {
            date: new Date().toISOString(),
            data: value,
        });
    };

    const get = (hash: string): T | undefined => {
        const cacheData = cacheStorage.get(hash);
        if (cacheData && !hasExpired(cacheData, revalidate)) {
            lruList.splice(lruList.indexOf(hash), 1);
            lruList.push(hash);
            return cacheData.data;
        } else {
            cacheStorage.delete(hash);
            lruList.splice(lruList.indexOf(hash), 1);
            return undefined;
        }
    };

    return {
        get,
        set,
    };
};
